'use client';

/**
 * useAssistantSession — entry intent, durable conversation identity, and the
 * ?prompt= release lifecycle.
 *
 * Extracted from the full-page route so the embedded panel
 * (`/us-en/ai-assistant/embed`) gets identical entry semantics rather than a
 * second implementation. This logic is subtle and the comments below record
 * real production bugs (SSR starter-grid flash, the location.replace
 * duplicate-send loop, StrictMode double-mint) — a parallel copy would
 * re-introduce them one by one, which is precisely why it lives here now.
 *
 * Call inside a ChatProvider. Side-effecting: it consumes the entry intent
 * into the conversation store and drives the prompt release. Call it ONCE per
 * mounted assistant surface.
 *
 * The pure classification lives in `@/lib/assistant-entry`; this hook is only
 * the React wiring around it.
 */

import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { useChatContext } from '@/contexts/ChatContext';
import { useConversationStore } from '@/stores/conversation-store';
import {
  deriveEntryIntent,
  EntryKind,
  PROMPT_RELEASE_TIMEOUT_MS,
  type EntryIntent,
} from '@/lib/assistant-entry';
import { ASSISTANT_TOPICS } from '@/lib/assistant-topics';
import { UIState, ConnectionStatus } from '@/types/chat';

export interface AssistantSession {
  /** Immutable entry classification, captured synchronously at first render. */
  entryIntent: EntryIntent;
  /** Has the store handoff (effect 1a) run? Gates pre-consumption render frames. */
  entryConsumed: boolean;
  /** Store intent flag: a ?c= resume still awaiting HISTORY_RESTORED. */
  expectingHistory: boolean;
  /** A ?c= resume whose intent the store hasn't adopted yet — show the skeleton. */
  resumeBooting: boolean;
  /** Live URL check for a not-yet-released ?prompt= (expires on its own). */
  hasQueuedPrompt: boolean;
  /** Durable conversation identity. */
  conversationId: string | null;
}

export function useAssistantSession(): AssistantSession {
  const { messages, uiState, connectionStatus, sendText, setHeadline } =
    useChatContext();

  // ── Entry intent: durable conversation identity + ?prompt= handling ──
  //
  // Two URL params drive mount: ?c=<conversationId> (durable identity, present
  // on reload/resume) and ?prompt=<query> (set by the "Ask Sara" popover on a
  // fresh visit). The FIRST render — the server-rendered HTML included — must
  // already know which entry this is (restore skeleton vs. starter grid vs.
  // incoming first message), so the intent is derived synchronously in a lazy
  // initializer, never in a mount effect: effects run after first paint, and
  // the SSR HTML paints even earlier. Captured once, BEFORE the URL-sync below
  // rewrites the params; immutable thereafter. See lib/assistant-entry.ts.
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const initFromUrl = useConversationStore((s) => s.initFromUrl);
  const conversationId = useConversationStore((s) => s.conversationId);
  // Intent flag: a resume awaiting HISTORY_RESTORED (drives the skeleton below).
  const expectingHistory = useConversationStore((s) => s.expectingHistory);
  // Intent flag: a "New chat" reset not yet consumed by the connection
  // machine's session-change effect. Subscribed here so effect (2) re-runs
  // when the reset completes (the immediate release must not race it — see
  // the guard in effect 2).
  const freshChatIntent = useConversationStore((s) => s.freshChatIntent);

  const [entryIntent] = useState<EntryIntent>(() =>
    deriveEntryIntent(searchParams.get('c'), searchParams.get('prompt'))
  );
  // Has effect (1a) handed the intent to the stores yet? State — not a ref —
  // because RENDER logic reads it (the restore-skeleton gate covers the
  // pre-consumption frames, SSR/hydration included; reading a mutable ref
  // during render is not render-safe).
  const [entryConsumed, setEntryConsumed] = useState(false);
  // Imperative once-guard for effect (1a). A ref alongside the state above
  // because StrictMode invokes the effect twice against the SAME first-render
  // closure (entryConsumed is false in both invocations) and startNewChat
  // must not run twice — it mints a fresh session each call.
  const entryGuardRef = useRef(false);
  // The ask riding on this entry, if any (PROMPT: auto-send; RESUME: deferred).
  const queuedPrompt =
    entryIntent.kind === EntryKind.PROMPT
      ? entryIntent.prompt
      : entryIntent.kind === EntryKind.RESUME
        ? entryIntent.queuedPrompt
        : null;
  const promptConsumedRef = useRef(false);
  const releaseTimerRef = useRef<number | null>(null);

  // (1a) Consume the entry intent — the single store handoff point.
  //
  // useLayoutEffect, NOT useEffect: this sets the flags the very first CLIENT
  // paint depends on (expectingHistory → restore skeleton). A plain effect
  // runs AFTER that paint, which flashed the starter cards for a frame
  // (owner report, 2026-07-13); the even-earlier SSR/pre-hydration frames are
  // covered by the entryConsumed render gate below.
  useLayoutEffect(() => {
    if (entryGuardRef.current) return;
    entryGuardRef.current = true;
    if (entryIntent.kind === EntryKind.RESUME) {
      // Adopt the ?c= so the very first settings frame sends it, and expect
      // the history replay: the backend resolves EVERY client-supplied ?c=
      // with exactly one of history_restored (possibly empty) /
      // conversation_reset / history_replay_failed; useAssistantConnection
      // also arms a timeout safety net in case the resolution never arrives
      // (older backends).
      initFromUrl(entryIntent.conversationId);
      useConversationStore.getState().setExpectingHistory(true);
    } else if (entryIntent.kind === EntryKind.PROMPT) {
      // Site-entry with a queued ?prompt= but no conversation identity (the
      // "Ask Sara" popover / site menu): this is a NEW ask — never append it
      // to whatever conversation happens to be active in the store.
      //
      // Deliberately NOT startNewChat()/startNewConversation() — that also
      // mints a new sessionId, which is for forcing a reconnect on an
      // ALREADY-CONNECTED page (the "New" button). This layout effect runs
      // before useAssistantConnection's mount effect has opened this mount's
      // first socket, so there's nothing to reconnect yet — minting a second
      // sessionId here only produced a spurious extra socket (open #1 under
      // the stale id, then immediately close it for #2 once the session-
      // change effect noticed the bump), a real connection-churn bug that
      // could drop an in-flight reply. startNewConversationForEntry() mints
      // a fresh conversationId (still a distinct "new ask" identity) and
      // resets everything startNewConversation() does EXCEPT sessionId — see
      // its doc comment in conversation-store.ts.
      useConversationStore.getState().startNewConversationForEntry();
    }
    setEntryConsumed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (1b) Keep the URL's ?c= in sync with the durable conversationId — the fresh-
  // visit mirror AND later in-app switches (clicking a past conversation in the
  // history popover changes conversationId). Strips the consumed ?prompt=.
  // Native history.replaceState, NOT router.replace: this is pure query-param
  // bookkeeping (no server render depends on ?c=/?prompt), and dispatching a
  // router navigation here — in the first frames after hydration, on a
  // prerendered page — could make Next bail out to a full MPA navigation
  // (location.replace) that re-landed on the pre-replace ?prompt= URL, which
  // re-minted a conversation and re-sent the prompt: a reload/duplicate-send
  // loop (observed on test7, 2026-07-21, up to 14 laps per entry; reproduced
  // in any production build). The native call is the App-Router-sanctioned
  // shallow update — no router action, no RSC fetch, URL committed
  // synchronously — and passing the current history.state through preserves
  // Next's internal history entry so back/forward stays SPA navigation.
  // replaceState, never pushState, so it adds no history entry and the back
  // button never replays ?prompt=. The guard makes it a no-op once in sync.
  useEffect(() => {
    // Read the id from the store at EFFECT TIME, not from this render's
    // closure. Effect (1a) above is a layout effect that adopts the ?c= into
    // the store during the SAME commit, so the `conversationId` captured by
    // this render is still the pre-adoption minted id — writing that would
    // replace a valid ?c= with a conversation the user never opened.
    const liveConversationId = useConversationStore.getState().conversationId;
    if (!liveConversationId) return;
    if (
      searchParams.get('c') === liveConversationId &&
      !searchParams.get('prompt')
    ) {
      return;
    }
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    params.set('c', liveConversationId);
    params.delete('prompt');
    window.history.replaceState(
      window.history.state,
      '',
      `${pathname}?${params.toString()}`
    );
  }, [conversationId, searchParams, pathname]);

  // (2) Release the prompt. Fresh conversation → send IMMEDIATELY at mount —
  // no waiting for the socket: the connection machine queues the send in its
  // outbox and drains it right after the settings frame on open, so the
  // user's message renders on frame one (with its "Sending…" status) instead
  // of flashing the starter grid for the duration of the handshake.
  // Resume-with-queued-prompt (rare; only a hand-crafted ?c=&prompt= URL) → wait:
  // a successful restore discards it (effect 3); a timeout releases it (the ?c=
  // was a brand-new conversation with no history to restore).
  const releasePrompt = useCallback(() => {
    if (promptConsumedRef.current) return;
    if (releaseTimerRef.current) {
      clearTimeout(releaseTimerRef.current);
      releaseTimerRef.current = null;
    }
    promptConsumedRef.current = true;
    if (!queuedPrompt) return;
    const matchingTopic = ASSISTANT_TOPICS.find(
      (t) => t.prompt === queuedPrompt
    );
    if (matchingTopic) setHeadline(matchingTopic.headline);
    sendText(queuedPrompt);
  }, [queuedPrompt, sendText, setHeadline]);

  useEffect(() => {
    if (promptConsumedRef.current || queuedPrompt == null) return;

    if (entryIntent.kind === EntryKind.PROMPT) {
      // Fresh visit — release without waiting for the connection (see the
      // outbox note in this effect's header comment). The freshChatIntent
      // check below is a defensive no-op on THIS path: entry effect (1a) now
      // mints via startNewConversationForEntry(), which deliberately never
      // sets freshChatIntent (see its doc comment in conversation-store.ts —
      // there's no session-change effect coming to flip it back to false,
      // since no sessionId changes here, so a set-true would wedge this
      // release forever). Left in only in case some unrelated in-flight flow
      // (e.g. a "New" click racing this same tick) happens to have it set.
      if (useConversationStore.getState().freshChatIntent) return;
      releasePrompt();
      return;
    }

    // Resume-with-queued-prompt: the restore resolution can only arrive once
    // connected, so only start the fallback-release clock from there.
    if (connectionStatus !== ConnectionStatus.CONNECTED) return;
    if (releaseTimerRef.current == null) {
      // Resuming with a queued prompt — arm the fallback release.
      releaseTimerRef.current = window.setTimeout(
        releasePrompt,
        PROMPT_RELEASE_TIMEOUT_MS
      );
    }
  }, [
    connectionStatus,
    freshChatIntent,
    queuedPrompt,
    entryIntent.kind,
    releasePrompt,
  ]);

  // (3) Restore resolved (history populated after a ?c= resume) → discard the
  // queued prompt so it isn't replayed into the resumed thread. Cancels the timer.
  useEffect(() => {
    if (promptConsumedRef.current) return;
    if (entryIntent.kind !== EntryKind.RESUME || queuedPrompt == null) return;
    if (uiState === UIState.CHAT && messages.length > 0) {
      promptConsumedRef.current = true;
      if (releaseTimerRef.current) {
        clearTimeout(releaseTimerRef.current);
        releaseTimerRef.current = null;
      }
    }
  }, [uiState, messages.length, entryIntent.kind, queuedPrompt]);

  // (4) Restore explicitly FAILED (backend HISTORY_REPLAY_FAILED) → release the
  // queued prompt NOW instead of waiting out the fallback timeout. The ?c= turned
  // out to have no usable history, so the user's prompt should be sent.
  const historyReplayFailed = useConversationStore(
    (s) => s.historyReplayFailed
  );
  useEffect(() => {
    if (
      historyReplayFailed &&
      entryIntent.kind === EntryKind.RESUME &&
      queuedPrompt != null &&
      !promptConsumedRef.current
    ) {
      releasePrompt();
    }
  }, [historyReplayFailed, entryIntent.kind, queuedPrompt, releasePrompt]);

  // Clear any pending release timer on unmount.
  useEffect(
    () => () => {
      if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    },
    []
  );

  // Live URL check (NOT the immutable entryIntent): the URL-sync effect
  // strips ?prompt= once the prompt is consumed, so this gate expires on its
  // own — entryIntent.kind would suppress the empty state forever, breaking
  // a later "New chat" (zero messages again, grid must show).
  const hasQueuedPrompt = !!searchParams.get('prompt');
  // A resume entry shows the restore skeleton from the very first RENDER —
  // the server-rendered HTML and the hydration pass would otherwise bake the
  // starter grid into a ?c= page for the pre-hydration frames (owner report,
  // 2026-07-13). Dies when effect (1a) consumes the intent; expectingHistory
  // (set in that same pre-paint layout effect) takes over seamlessly, so an
  // empty restore / conversation_reset still ends the skeleton normally.
  const resumeBooting = entryIntent.kind === EntryKind.RESUME && !entryConsumed;

  return {
    entryIntent,
    entryConsumed,
    expectingHistory,
    resumeBooting,
    hasQueuedPrompt,
    conversationId,
  };
}
